"use client";

import { ProcessingJobType } from "@prisma/client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Components } from "@/app/(components)/_components/Components";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import NextStepCard from "@/app/(components)/generics/cards/NextStepCard/NextStepCard";
import IconUser from "@/app/(components)/generics/Icons/IconUser";
import CircleLoader from "@/app/(components)/generics/loaders/CircleLoader/CircleLoader";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import OfferCard from "@/app/(components)/specifics/tools/Offers/components/OfferCard/OfferCard";
import OffersEmpty from "@/app/(components)/specifics/tools/Offers/components/OffersEmpty/OffersEmpty";
import OffersTeaser from "@/app/(components)/specifics/tools/Offers/components/OffersTeaser/OffersTeaser";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import {
  useCanEditContent,
  useCanRunPaidActions,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import PageWrapper from "@/app/(components)/specifics/wrappers/PageWrapper/PageWrapper";
import { useFetchOffersBootstrap } from "@/app/hooks/fetchs/offers/useFetchOffersBootstrap";
import { useFetchProjectOffers } from "@/app/hooks/fetchs/offers/useFetchProjectOffers";
import { useFetchHasProjectInsights } from "@/app/hooks/fetchs/project/useFetchHasProjectInsights";
import { useFetchRunningJobs } from "@/app/hooks/fetchs/user/useFetchRunningJobs";
import { ModalStore } from "@/app/stores/ModalStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { PageBootstrap } from "@/app/types/common/WorkspaceSelection";
import { OfferExtractionJobMetadata } from "@/app/types/job/ProcessingJob";
import { OffersBootstrap } from "@/app/types/offer/OffersBootstrap";
import { URLs } from "@/app/utils/URLs";
import { Limits } from "@/app/vars/Limits";
import { QueryKey } from "@/app/vars/QueryKeys";
import { Routes } from "@/app/vars/Routes";
import styles from "./Offers.module.css";

type Props = DefaultProps & {
  bootstrap: PageBootstrap<OffersBootstrap> | null;
};

export default function Offers(props: Props) {
  const { params, bootstrap } = props;

  const T = useT();
  const user = useAuthUser();
  const project = ProjectStore((state) => state.currentProject);
  const projectsFetched = ProjectStore((state) => state.projectsFetched);
  const canManage = useCanEditContent();
  const canRunPaidActions = useCanRunPaidActions();
  const queryClient = useQueryClient();

  const initialBootstrap = bootstrap?.projectId === project?.id ? bootstrap?.data : undefined;

  const { isSuccess: bootstrapReady } = useFetchOffersBootstrap({
    projectId: project?.id,
    enabled: !!user && !!project?.id,
    initialData: initialBootstrap,
  });

  const gateEnabled = !!user && !!project?.id && bootstrapReady;

  const { data: fetchedOffers } = useFetchProjectOffers({
    projectId: project?.id,
    enabled: gateEnabled,
  });

  const { data: hasInsights } = useFetchHasProjectInsights(project?.id, gateEnabled);
  const { data: runningJobs } = useFetchRunningJobs({ enabled: !!user });

  const offers = fetchedOffers || [];
  const atLimit = offers.length >= Limits.MAX_PROJECT_OFFERS;
  const isReady = projectsFetched && (!project?.id || fetchedOffers !== undefined);
  const isOddOffers = offers.length % 2 !== 0;

  const avatarNextStep = (
    <NextStepCard
      params={params}
      icon={<IconUser />}
      title={T(TKey.OFFERS_NEXT_AVATAR_TITLE) as string}
      description={T(TKey.OFFERS_NEXT_AVATAR_DESCRIPTION) as string}
      ctaLabel={T(TKey.OFFERS_NEXT_AVATAR_CTA) as string}
      href={URLs.pathOf(Routes.PROTECTED.CLIENT_AVATAR)}
    />
  );

  const isExtracting = useMemo(() => {
    if (!runningJobs || !project?.id) {
      return false;
    }
    return runningJobs.some(
      (j) =>
        j.type === ProcessingJobType.OFFER_EXTRACTION &&
        (j.metadata as OfferExtractionJobMetadata | null)?.projectId === project.id,
    );
  }, [runningJobs, project?.id]);

  const invalidate = useCallback(() => {
    if (project?.id) {
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROJECT_OFFER, project.id] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.CLIENT_AVATAR] });
    }
  }, [queryClient, project?.id]);

  const openOfferForm = useCallback(() => {
    if (!project?.id) {
      return;
    }

    ModalStore.getState().openModal(
      Components.OfferForm,
      T(TKey.PROJECT_OFFER_ADD) as string,
      { params, projectId: project.id, isCreating: true, onAfterSave: invalidate },
      ModalType.MEDIUM,
    );
  }, [params, project?.id, invalidate, T]);

  const openExtraction = useCallback(() => {
    if (!project?.id) {
      return;
    }

    ModalStore.getState().openModal(
      Components.OfferExtraction,
      T(TKey.OFFERS_EXTRACT_TITLE) as string,
      {
        params,
        projectId: project.id,
        hasInsights: hasInsights ?? false,
        hasExistingOffers: offers.length > 0,
      },
      ModalType.SMALL,
    );
  }, [params, project?.id, hasInsights, offers.length, T]);

  const handleAdd = useCallback(() => {
    if (!project?.id) {
      return;
    }

    if (!canRunPaidActions) {
      openOfferForm();
      return;
    }

    ModalStore.getState().openModal(
      Components.OfferAddChoice,
      T(TKey.PROJECT_OFFER_ADD) as string,
      {
        params,
        hasInsights: hasInsights ?? false,
        onExtract: openExtraction,
        onManual: openOfferForm,
      },
      ModalType.SMALL,
    );
  }, [params, project?.id, canRunPaidActions, hasInsights, openExtraction, openOfferForm, T]);

  return (
    <PageWrapper title={T(TKey.OFFERS_TITLE)} subtitle={T(TKey.OFFERS_SUBTITLE)}>
      <div className={styles.wrapper}>
        {!user ? (
          <OffersTeaser params={params} />
        ) : !isReady ? (
          <div className={styles.loaderContainer}>
            <PointsLoader color="var(--color-green-3)" delay={Limits.CLIENT_AVATAR_LOADER_DELAY} />
          </div>
        ) : (
          <div className={styles.container}>
            <div className={styles.header}>
              <span className={styles.count}>
                {offers.length} / {Limits.MAX_PROJECT_OFFERS}
              </span>
              {canManage && !atLimit && (
                <Button
                  min={true}
                  isLink={false}
                  type={ButtonType.OUTLINE}
                  text={T(TKey.PROJECT_OFFER_ADD) as string}
                  onClick={handleAdd}
                />
              )}
            </div>

            {offers.length === 0 ? (
              isExtracting ? (
                <div className={styles.extracting}>
                  <CircleLoader size={20} color="var(--color-green-3)" strokeWidth={3} />
                  <span className={styles.extractingText}>{T(TKey.OFFERS_EXTRACTING)}</span>
                </div>
              ) : (
                <OffersEmpty
                  params={params}
                  canManage={canManage}
                  onExtract={canRunPaidActions ? openExtraction : undefined}
                />
              )
            ) : (
              <>
                <div className={styles.offersList}>
                  {offers.map((offer) => (
                    <OfferCard
                      key={offer.id}
                      params={params}
                      offer={offer}
                      projectId={project!.id}
                      onSaved={invalidate}
                      onDeleted={invalidate}
                    />
                  ))}
                  {isOddOffers && <div className={styles.avatarSlot}>{avatarNextStep}</div>}
                </div>

                {!isOddOffers && avatarNextStep}
              </>
            )}
          </div>
        )}
      </div>
    </PageWrapper>
  );
}
